// src/pages/IdGraphPage.jsx
// Identity graph — privacy/identity debugging over the live captured session.
// Scans every captured row for device/user IDs (Section 1: who leaks where) and
// supply-chain (schain) declarations (Section 2: supply-chain completeness).
// Rendered as the "IDs & supply chain" tab of PrivacyHubPage (/privacy?tab=ids).
import React, { useMemo, useState, useCallback } from 'react';
import { useTraffic } from '../context/TrafficContext';
import { buildIdGraph } from '../utils/idGraph';
import { findSchains } from '../utils/schainParse';

const FLAG_STYLE = {
  leak: { bg: 'var(--error-bg)', fg: '#e5484d' },
  inconsistent: { bg: 'var(--warn-bg)', fg: '#d97706' },
  zeroed: { bg: 'var(--surface)', fg: '#8b8d98' },
};

function flagStyleFor(flag) {
  if (flag.startsWith('leak')) return FLAG_STYLE.leak;
  if (flag.startsWith('inconsistent')) return FLAG_STYLE.inconsistent;
  if (flag === 'zeroed') return FLAG_STYLE.zeroed;
  return FLAG_STYLE.inconsistent;
}

function Chip({ children, bg, fg, title }) {
  return (
    <span
      title={title}
      style={{
        display: 'inline-block', fontSize: 10.5, fontWeight: 600, margin: '2px 4px 2px 0',
        padding: '2px 8px', borderRadius: 20,
        color: fg || 'var(--muted)', background: bg || 'var(--surface)',
        border: bg ? 'none' : '1px solid var(--line)',
      }}
    >
      {children}
    </span>
  );
}

function IdRow({ id }) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback((e) => {
    e.stopPropagation();
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(id.value);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch { /* ignore clipboard failures */ }
  }, [id.value]);

  return (
    <tr style={{ borderTop: '1px solid var(--line)' }}>
      <td style={td}>{id.idType}</td>
      <td style={{ ...td, fontFamily: 'monospace' }}>
        <span
          onClick={() => setRevealed((r) => !r)}
          style={{ cursor: 'pointer' }}
          title={revealed ? 'Click to mask' : 'Click to reveal full value'}
        >
          {revealed ? id.value : id.maskedValue}
        </span>
        <button
          onClick={handleCopy}
          style={{
            marginLeft: 8, fontSize: 10, padding: '1px 6px', borderRadius: 5,
            border: '1px solid var(--line)', background: 'var(--surface)',
            color: 'var(--muted)', cursor: 'pointer',
          }}
        >
          {copied ? 'copied' : 'copy'}
        </button>
      </td>
      <td style={td}>
        {id.sources.map((s, i) => (
          <Chip key={i} title={`${s.host} · ${s.requestType}`}>
            {s.host} × {s.count}
          </Chip>
        ))}
      </td>
      <td style={td}>
        {id.flags.length === 0
          ? <span style={{ color: 'var(--muted)', fontSize: 11 }}>—</span>
          : id.flags.map((f, i) => {
            const st = flagStyleFor(f);
            return <Chip key={i} bg={st.bg} fg={st.fg}>{f}</Chip>;
          })}
      </td>
    </tr>
  );
}

const COMPLETE_BADGE = {
  true: { bg: 'var(--success-bg)', fg: '#1a7f37', label: 'complete' },
  false: { bg: 'var(--warn-bg)', fg: '#d97706', label: 'incomplete' },
  null: { bg: 'var(--error-bg)', fg: '#e5484d', label: 'unparseable' },
};

function SchainCard({ entry }) {
  const { host, requestType, parsed } = entry;
  const badgeKey = parsed.ok ? String(parsed.complete) : 'null';
  const badge = COMPLETE_BADGE[badgeKey] || COMPLETE_BADGE.null;
  const hasIssues = parsed.issues && parsed.issues.length > 0;

  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10,
      padding: '12px 14px',
      borderLeft: `4px solid ${hasIssues && parsed.complete !== false ? '#e5484d' : badge.fg}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <b style={{ fontSize: 13 }}>{host}</b>
        <span style={{ fontSize: 11, color: 'var(--muted)' }}>{requestType}</span>
        <span style={{
          marginLeft: 'auto', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
          color: badge.fg, background: badge.bg,
        }}>
          {badge.label}
        </span>
      </div>

      {parsed.nodes && parsed.nodes.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 12, fontFamily: 'monospace', color: 'var(--ink)', overflowX: 'auto', whiteSpace: 'nowrap' }}>
          {parsed.nodes.map((n, i) => (
            <span key={i}>
              {i > 0 && <span style={{ color: 'var(--muted)' }}> → </span>}
              {n.asi || '?'} <span style={{ color: 'var(--muted)' }}>({n.sid || '?'})</span>
            </span>
          ))}
        </div>
      )}

      {hasIssues && (
        <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 11.5, color: '#d97706' }}>
          {parsed.issues.map((iss, i) => <li key={i}>{iss}</li>)}
        </ul>
      )}
    </div>
  );
}

const th = {
  textAlign: 'left', padding: '8px 10px', fontWeight: 600, whiteSpace: 'nowrap',
};
const td = {
  padding: '8px 10px', verticalAlign: 'top', fontSize: 12.5,
};

export default function IdGraphPage() {
  const traffic = useTraffic();
  const liveTrafficGroups = traffic ? traffic.liveTrafficGroups : {};

  const [rescanNonce, setRescanNonce] = useState(0);

  const allRows = useMemo(
    () => Object.values(liveTrafficGroups || {}).flat(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [liveTrafficGroups, rescanNonce],
  );

  const idGraph = useMemo(() => {
    try { return buildIdGraph(allRows); }
    catch { return { ids: [], attSignals: [], summary: { total: 0, leaks: 0 } }; }
  }, [allRows]);

  const schains = useMemo(() => {
    try { return findSchains(allRows); }
    catch { return []; }
  }, [allRows]);

  const handleRescan = useCallback(() => setRescanNonce((n) => n + 1), []);

  const isEmpty = allRows.length === 0;

  return (
    <div style={{ padding: 20, display: 'grid', gap: 16, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, color: 'var(--ink)' }}>Identity Graph</h2>
          <p style={{ margin: '4px 0 0 0', color: 'var(--muted)', fontSize: 13 }}>
            Which user/device IDs leak where, and supply-chain (schain) completeness —
            scanned over the current live session.
          </p>
        </div>
        <button
          onClick={handleRescan}
          style={{
            marginLeft: 'auto', fontSize: 13, padding: '8px 16px', borderRadius: 8,
            border: '1px solid var(--line)', background: 'var(--surface)',
            color: 'var(--ink)', cursor: 'pointer', fontWeight: 600,
          }}
        >
          ↻ Rescan
        </button>
      </div>

      {isEmpty && (
        <div style={{
          background: 'var(--surface)', border: '1px dashed var(--line)', borderRadius: 10,
          padding: '28px 20px', textAlign: 'center', color: 'var(--muted)',
        }}>
          No live traffic yet. Connect a device and browse the app — identifiers and supply
          chains found in captured requests will show up here.
        </div>
      )}

      {/* Section 1: Identity graph */}
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: 'var(--ink)' }}>Identity graph</h3>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>
            {idGraph.summary.total} ID{idGraph.summary.total === 1 ? '' : 's'}
            {idGraph.summary.leaks > 0 && (
              <> · <span style={{ color: '#e5484d', fontWeight: 600 }}>{idGraph.summary.leaks} leak flag{idGraph.summary.leaks === 1 ? '' : 's'}</span></>
            )}
          </span>
        </div>

        {idGraph.ids.length > 0 && (
          <div style={{ border: '1px solid var(--line)', borderRadius: 10, overflow: 'hidden', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead>
                <tr style={{ background: 'var(--surface)', color: 'var(--muted)', fontSize: 11 }}>
                  <th style={th}>Type</th>
                  <th style={th}>Value</th>
                  <th style={th}>Seen at</th>
                  <th style={th}>Flags</th>
                </tr>
              </thead>
              <tbody>
                {idGraph.ids.map((id, i) => (
                  <IdRow key={`${id.idType}-${id.value}-${i}`} id={id} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {idGraph.attSignals.length > 0 && (
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--muted)' }}>
            <b style={{ color: 'var(--ink)' }}>ATT / limit-tracking signals: </b>
            {idGraph.attSignals.map((s, i) => (
              <Chip key={i} title={s.source}>{s.source} = {s.value}</Chip>
            ))}
          </div>
        )}
      </div>

      {/* Section 2: Supply chains */}
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: 'var(--ink)' }}>Supply chains</h3>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>
            {schains.length} chain{schains.length === 1 ? '' : 's'}
          </span>
        </div>

        {schains.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 10 }}>
            {schains.map((entry, i) => (
              <SchainCard key={`${entry.host}-${i}`} entry={entry} />
            ))}
          </div>
        )}

        {!isEmpty && schains.length === 0 && (
          <div style={{ color: 'var(--muted)', fontSize: 12.5 }}>
            No schain declarations found in the current session.
          </div>
        )}
      </div>
    </div>
  );
}
